/**
 * useConnectors - Hook for managing connector data
 *
 * Uses React Query for data fetching with the standard admin API patterns.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import type { Connector, ConnectorType, ConnectorConfig, ConnectorSchedule, ConnectorStatus, ScheduleUpdateRequest, SchedulePreviewResponse } from '@/types/connectors';
import type { CredentialFlags, ConnectionFieldValues } from '@/components/connectors/shared/types';
import { DEFAULT_SCHEDULE_TIMEZONE } from '@/constants/timezone';

const API_BASE = '/connectors';

interface ConnectorListResponse {
  connectors: Connector[];
  total: number;
}

/**
 * Error type for 409 Conflict responses during credential updates.
 * Occurs when the resource has been modified since the ETag was retrieved.
 */
export interface ConflictError {
  /** Discriminator to identify conflict errors */
  isConflict: true;
  /** The current ETag of the resource (server's version) */
  currentEtag: string;
  /** Human-readable error message */
  message: string;
}

/**
 * Type guard to check if an error is a ConflictError.
 */
export function isConflictError(error: unknown): error is ConflictError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isConflict' in error &&
    (error as ConflictError).isConflict === true
  );
}

/**
 * Parameters for updating connector credentials.
 */
export interface UpdateCredentialsParams {
  /** The connector type (e.g., 'google_ads', 'hubspot') */
  connectorType: ConnectorType;
  /** The connector configuration name */
  name: string;
  /** Partial credential values to update */
  credentials: Partial<ConnectionFieldValues>;
  /** ETag for optimistic concurrency control (from If-Match header) */
  etag?: string;
}

/**
 * Options for the useUpdateCredentials hook.
 */
export interface UpdateCredentialsOptions {
  /** Callback invoked when a 409 Conflict error occurs */
  onConflict?: (error: ConflictError) => void;
}

/**
 * Extended response from connector details endpoint.
 * Includes credential flags and connection settings for editing.
 */
export interface ConnectorDetailsResponse {
  connector: Connector;
  credentialFlags: CredentialFlags | undefined;
  connection: Record<string, unknown> | undefined;
  etag: string | undefined;
  /**
   * Google Ads only: resource keys referenced by this connector's
   * configuration that are not present in the canonical catalog. Always
   * an array (possibly empty) for google_ads connectors; empty for others.
   */
  unknownResources: string[];
  /**
   * Google Ads only: true when the stored credentials predate the OAuth
   * popup migration and must be refreshed before extractions will work.
   */
  reauthRequired: boolean;
  /** Captured at OAuth completion (#140). null for non-OAuth connectors or connectors authorized before this MR landed. */
  oauthIdentity: string | null;
}

/**
 * Connector types that support credential editing via PATCH /credentials.
 * google_ads persists its account identifiers (customer_id, login_customer_id)
 * through the credentials route instead of plaintext settings (#597).
 * Other OAuth connectors manage credentials through their OAuth flows.
 */
// s3_csv (#2062): without this the detail editor folds an edited AWS secret key
// into PLAINTEXT settings. Its bucket/region/prefix also live in the credential
// store (the wizard's credential test needs them), and PATCH /credentials
// merges, so editing only the bucket keeps the stored secret.
// vistar (#2075): same reason -- an edited password must go to the credential
// store, never into plaintext settings.
const CREDENTIAL_EDITING_CONNECTORS: ReadonlyArray<string> = [
  'google_ads',
  's3_csv',
  'vistar',
];

/**
 * Type guard to check if a connector type supports credential editing.
 * @param connectorType - The connector type string to check
 * @returns true if the connector supports direct credential editing
 */
export function supportsCredentialEditing(connectorType: string): boolean {
  return CREDENTIAL_EDITING_CONNECTORS.includes(connectorType);
}

/**
 * Execution states that mean an extraction is currently in flight.
 * `latest_execution.status` (repository ExecutionStatus) is lowercase and
 * emits `pending` / `running`; `started` is included per issue #901 to
 * future-proof against any Celery-level status leaking through — it is
 * harmless since all three unambiguously mean "in flight".
 */
export const IN_FLIGHT_EXECUTION_STATUSES = ['pending', 'started', 'running'] as const;

/**
 * Derive a connector's display status for the Connectors status page.
 *
 * Priority (highest to lowest), matching connectors/api/status_calculator.py
 * minus the health/staleness inputs the list/details payloads don't carry:
 *   1. disabled  — not enabled (unaffected by an in-flight execution)
 *   2. running   — latest execution is in flight (pending/started/running)
 *   3. error     — last execution failed
 *   4. connected — otherwise
 *
 * Exported separately so it can be unit-tested without spinning up React
 * (mirrors `computeRefetchInterval`).
 */
export function deriveConnectorStatus(params: {
  enabled: boolean;
  lastExecutionStatus?: string | null;
  latestExecutionStatus?: string | null;
}): ConnectorStatus {
  if (!params.enabled) return 'disabled';
  if (
    params.latestExecutionStatus &&
    (IN_FLIGHT_EXECUTION_STATUSES as readonly string[]).includes(params.latestExecutionStatus)
  ) {
    return 'running';
  }
  return params.lastExecutionStatus === 'failed' ? 'error' : 'connected';
}

/**
 * Fetch all connectors
 */
async function fetchConnectors(enabledOnly: boolean = false): Promise<ConnectorListResponse> {
  const response = await apiClient.get(`${API_BASE}/`, {
    params: { enabled_only: enabledOnly },
  });

  const connectors = (response.data.connectors || []).map((c: any) => {
    // Handle both response formats: nested configuration or flat
    const config = c.configuration || c;

    // Map latest execution data from backend
    const latestExecution = c.latest_execution ? {
      id: c.latest_execution.id,
      execution_id: c.latest_execution.execution_id,
      connector_type: c.connector_type,
      connector_config_id: c.id,
      status: c.latest_execution.status,
      trigger_type: c.latest_execution.trigger_type || 'manual',
      triggered_by: c.latest_execution.triggered_by,
      triggered_by_label: c.latest_execution.triggered_by_label,
      started_at: c.latest_execution.started_at,
      completed_at: c.latest_execution.completed_at,
      duration_seconds: c.latest_execution.duration_seconds,
      files_created: c.latest_execution.files_created || 0,
      tables_updated: c.latest_execution.tables_updated || 0,
      records_processed: c.latest_execution.records_processed || 0,
      bytes_written: c.latest_execution.bytes_written || 0,
      queries_successful: c.latest_execution.queries_successful || 0,
      queries_failed: c.latest_execution.queries_failed || 0,
      error_message: c.latest_execution.error_message,
      error_details: c.latest_execution.error_details,
      output_paths: c.latest_execution.output_paths,
    } : undefined;

    return {
      // #970: never fabricate a discriminator from the connector_type slug.
      // The old `|| c.connector_type` fallbacks turned a missing id/name into a
      // silent collision — two same-type rows collapsing to the same route/key
      // and misrouting the second to the first. Preserve the real (possibly
      // empty) values so a missing discriminator surfaces instead.
      id: c.id != null ? String(c.id) : undefined,
      connectorType: c.connector_type,
      name: c.name ?? '',
      description: config.description || c.description,
      status: deriveConnectorStatus({
        enabled: config.enabled ?? c.enabled,
        lastExecutionStatus: c.last_execution_status,
        latestExecutionStatus: latestExecution?.status,
      }),
      enabled: config.enabled ?? c.enabled,
      config: {
        enabled: config.enabled ?? c.enabled,
        max_workers: config.max_workers ?? c.max_workers,
        schedule_cron: config.schedule_cron ?? c.schedule_cron,
        // #1091: normalized to undefined, unlike the sibling lines. The backend
        // emits JSON null (not an absent key) when a config has no schedule row,
        // and describeCron's default parameter fires only on undefined — a null
        // would reach shortZoneLabel and render a literal "null" in the label.
        schedule_timezone: config.schedule_timezone ?? c.schedule_timezone ?? undefined,
        schedule_enabled: config.schedule_enabled ?? c.schedule_enabled,
        schedule: config.schedule_cron ?? c.schedule_cron,
      },
      latest_execution: latestExecution,
      scheduleEnabled: config.schedule_enabled ?? c.schedule_enabled,
      nextScheduledRun: c.next_run_at,
    };
  });
  return { connectors, total: connectors.length };
}

/**
 * Fetch details for a specific connector.
 * Returns extended response including credential flags and ETag for optimistic concurrency.
 */
async function fetchConnectorDetails(connectorType: ConnectorType, name: string): Promise<ConnectorDetailsResponse> {
  const response = await apiClient.get(`${API_BASE}/${connectorType}/${encodeURIComponent(name)}`);
  const c = response.data;

  // Extract ETag: prefer response body (always available), fall back to header
  const etag = c.etag || response.headers?.etag || response.headers?.['etag'] || undefined;

  // Extract configuration object - backend returns nested structure
  const config = c.configuration || {};

  // Extract credential flags if present (indicates which sensitive credentials exist)
  const credentialFlags: CredentialFlags | undefined = c.credential_flags;

  // Extract connection settings (with nullified sensitive values)
  const connection: Record<string, unknown> | undefined = c.connection;

  const connector: Connector = {
    // The configuration UUID lives at c.configuration.id in the details
    // response — the top-level c.id is not populated. Without this, mutations
    // that take a configurationId (Run Now, Delete) would receive
    // c.connector_type as a fallback string and the backend 404s with
    // "Configuration Not Found".
    id: config.id ? String(config.id) : (c.id ? String(c.id) : c.connector_type),
    connectorType: c.connector_type,
    name: c.name || config.name || c.connector_type,
    description: config.description,
    status: deriveConnectorStatus({
      enabled: config.enabled,
      lastExecutionStatus: c.last_execution_status,
      latestExecutionStatus: c.latest_execution?.status,
    }),
    enabled: config.enabled,
    config: {
      enabled: config.enabled,
      max_workers: config.max_workers,
      schedule_cron: config.schedule_cron,
      schedule_enabled: config.schedule_enabled,
      settings: config.config,
    },
    latest_execution: c.latest_execution ? {
      id: c.latest_execution.id,
      execution_id: c.latest_execution.execution_id,
      connector_type: c.connector_type,
      connector_config_id: c.id,
      status: c.latest_execution.status,
      trigger_type: c.latest_execution.trigger_type || 'manual',
      triggered_by: c.latest_execution.triggered_by,
      triggered_by_label: c.latest_execution.triggered_by_label,
      started_at: c.latest_execution.started_at,
      completed_at: c.latest_execution.completed_at,
      duration_seconds: c.latest_execution.duration_seconds,
      files_created: c.latest_execution.files_created || 0,
      tables_updated: c.latest_execution.tables_updated || 0,
      records_processed: c.latest_execution.records_processed || 0,
      bytes_written: c.latest_execution.bytes_written || 0,
      queries_successful: c.latest_execution.queries_successful || 0,
      queries_failed: c.latest_execution.queries_failed || 0,
      error_message: c.latest_execution.error_message,
      error_details: c.latest_execution.error_details,
      output_paths: c.latest_execution.output_paths,
    } : undefined,
    laggingResources: Array.isArray(c.lagging_resources) ? c.lagging_resources : [],
    scheduleEnabled: config.schedule_enabled,
    created_at: config.created_at || c.created_at,
    updated_at: config.updated_at || c.updated_at,
  };

  const unknownResources: string[] = Array.isArray(c.unknown_resources)
    ? c.unknown_resources
    : [];
  const reauthRequired: boolean = Boolean(c.reauth_required);
  const oauthIdentity: string | null =
    typeof c.oauth_identity === 'string' ? c.oauth_identity : null;

  return {
    connector,
    credentialFlags,
    connection,
    etag,
    unknownResources,
    reauthRequired,
    oauthIdentity,
  };
}

/**
 * Update connector configuration
 */
async function updateConnector(
  connectorType: ConnectorType,
  name: string,
  config: Partial<ConnectorConfig>
): Promise<Connector> {
  const response = await apiClient.patch(`${API_BASE}/${connectorType}/${encodeURIComponent(name)}`, config);
  return response.data;
}

/**
 * Response from credential update endpoint.
 */
interface UpdateCredentialsResponse {
  success: boolean;
  message: string;
  updated_fields: string[];
}

/**
 * Update connector credentials with ETag-based optimistic concurrency control.
 *
 * @param params - The update parameters including credentials and optional ETag
 * @returns The update response on success
 * @throws ConflictError if a 409 status is returned (resource was modified)
 * @throws Error for other API errors
 */
async function updateCredentials(params: UpdateCredentialsParams): Promise<UpdateCredentialsResponse> {
  const { connectorType, name, credentials, etag } = params;

  // Build request headers, including If-Match for ETag-based concurrency control
  const headers: Record<string, string> = {};
  if (etag) {
    headers['If-Match'] = etag;
  }

  try {
    const response = await apiClient.patch(
      `${API_BASE}/${connectorType}/${encodeURIComponent(name)}/credentials`,
      credentials,
      { headers }
    );
    return response.data;
  } catch (error: any) {
    // Handle 409 Conflict - resource was modified since ETag was retrieved
    if (error?.response?.status === 409) {
      const responseData = error.response.data || {};
      const conflictError: ConflictError = {
        isConflict: true,
        currentEtag: responseData.current_etag || '',
        message: responseData.message || responseData.detail || 'Resource was modified by another request',
      };
      throw conflictError;
    }
    // Re-throw other errors as-is
    throw error;
  }
}

/**
 * Trigger extraction for a connector
 */
async function triggerExtraction(params: { connectorType: ConnectorType; configurationId?: string }): Promise<{ execution_id: string }> {
  const { connectorType, configurationId } = params;

  // Build request payload based on connector type
  const requestBody: Record<string, any> = {
    configuration_id: configurationId,
  };

  // Query-based connectors (Google Ads, Google Analytics) use all_queries
  if (['google_ads', 'google_analytics'].includes(connectorType)) {
    requestBody.all_queries = true;
  }
  // Other connectors (Bing Ads, LinkedIn Ads, Facebook Ads, Shopify) may not require specific flags
  // The backend will handle connector-specific logic based on connector type

  const response = await apiClient.post(`${API_BASE}/${connectorType}/extract`, requestBody);
  return response.data;
}

/**
 * Cancel a pending or running connector execution.
 *
 * The backend route at `api/routes/connectors.py:cancel_execution` revokes
 * the Celery task with `terminate=True` and updates the execution row to
 * `status='cancelled'`. Returns 409 if the execution is no longer
 * cancellable (already completed/failed/cancelled).
 */
async function cancelExecution(executionId: string): Promise<{ status: string; message?: string }> {
  const response = await apiClient.post(
    `${API_BASE}/executions/${executionId}/cancel`,
    {},
  );
  return response.data;
}

/**
 * Check health status for a connector
 */
async function checkHealth(connectorType: ConnectorType): Promise<{ status: string }> {
  const response = await apiClient.get(`${API_BASE}/${connectorType}/health`);
  return response.data;
}

/**
 * Compute the React Query refetch interval given the most recent response.
 * Used for adaptive polling — 5s when any connector is running, 30s otherwise.
 *
 * Exported separately so it can be unit-tested without spinning up React.
 */
export function computeRefetchInterval(data: ConnectorListResponse | undefined): number {
  if (!data?.connectors) return 30000;
  const anyRunning = data.connectors.some((c: any) => c.status === 'running');
  return anyRunning ? 5000 : 30000;
}

/**
 * Hook to fetch and manage connector list
 */
export function useConnectors(enabledOnly: boolean = false) {
  return useQuery({
    queryKey: ['connectors', { enabledOnly }],
    queryFn: () => fetchConnectors(enabledOnly),
    staleTime: 30000,
    refetchInterval: (query) => computeRefetchInterval(query.state.data as ConnectorListResponse | undefined),
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });
}

/**
 * Hook to trigger connector extraction
 */
export function useTriggerExtraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { connectorType: ConnectorType; configurationId?: string }) => triggerExtraction(params),
    onSuccess: (_data, variables) => {
      // Invalidate connector list immediately to show task was queued
      queryClient.invalidateQueries({ queryKey: ['connectors'] });

      // Also invalidate the details query so the detail page refetches
      // immediately and the running execution surfaces in ~one poll cycle
      // instead of up to 30s (#65).
      queryClient.invalidateQueries({ queryKey: ['connector', variables.connectorType] });

      // The Celery task creates the execution record asynchronously when it starts.
      // We need to delay cache invalidation to give the task time to create the record.
      // First invalidation after 1.5 seconds (task should have started)
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['executionHistory', variables.connectorType] });
        queryClient.invalidateQueries({ queryKey: ['latestExecution', variables.connectorType] });
        queryClient.invalidateQueries({ queryKey: ['connectorMetrics', variables.connectorType] });
        queryClient.invalidateQueries({ queryKey: ['outputFiles', variables.connectorType] });
      }, 1500);

      // Second invalidation after 5 seconds to catch slower tasks
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['executionHistory', variables.connectorType] });
        queryClient.invalidateQueries({ queryKey: ['latestExecution', variables.connectorType] });
        queryClient.invalidateQueries({ queryKey: ['connectorMetrics', variables.connectorType] });
        queryClient.invalidateQueries({ queryKey: ['connectors'] });
      }, 5000);
    },
  });
}

/**
 * Hook to cancel a running/pending connector execution.
 *
 * Invalidates the same query keys as `useTriggerExtraction` so the UI
 * sees the status flip from running → cancelled without a manual refresh.
 */
export function useCancelExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (executionId: string) => cancelExecution(executionId),
    onSuccess: () => {
      // Invalidate broadly — we don't know the connector_type in scope here,
      // and the backend status flip may affect any list/detail views.
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
      queryClient.invalidateQueries({ queryKey: ['executionHistory'] });
      queryClient.invalidateQueries({ queryKey: ['latestExecution'] });
      queryClient.invalidateQueries({ queryKey: ['connectorMetrics'] });
    },
  });
}

/**
 * Hook to check connector health
 */
export function useHealthCheck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectorType: ConnectorType) => checkHealth(connectorType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
  });
}

/**
 * Test connector credentials without saving
 */
async function testCredentials(
  connectorType: ConnectorType,
  credentials: Record<string, any>
): Promise<{ success: boolean; message?: string; details?: any }> {
  const response = await apiClient.post(
    `${API_BASE}/${connectorType}/test-credentials`,
    { credentials }
  );
  return response.data;
}

/**
 * Hook to test connector credentials
 */
export function useTestCredentials() {
  return useMutation({
    mutationFn: ({ connectorType, credentials }: {
      connectorType: ConnectorType;
      credentials: Record<string, any>;
    }) => testCredentials(connectorType, credentials),
  });
}

/**
/**
 * Compute the React Query refetchInterval for a connector-details poll.
 * 5s while the latest execution is active (pending or running), 30s otherwise.
 */
export function computeConnectorDetailsRefetchInterval(
  data: { connector?: { latest_execution?: { status?: string } } } | undefined,
): number {
  const s = data?.connector?.latest_execution?.status;
  return (s === 'pending' || s === 'running') ? 5000 : 30000;
}

/**
 * Hook to fetch connector details with credential flags and ETag.
 *
 * Returns:
 * - data.connector: The connector details
 * - data.credentialFlags: Boolean flags indicating which credentials exist
 * - data.connection: Connection settings with nullified sensitive values
 * - data.etag: ETag header for optimistic concurrency control
 *
 * @param connectorType - The type of connector (e.g., 'google_ads', 'hubspot')
 * @param name - The connector configuration name
 * @param enabled - Whether to enable the query (default: true)
 */
export function useConnectorDetails(connectorType: string, name: string, enabled: boolean = true) {
  const query = useQuery({
    queryKey: ['connector', connectorType, name],
    queryFn: () => fetchConnectorDetails(connectorType as ConnectorType, name),
    enabled: enabled && !!connectorType && !!name,
    staleTime: 30000, // 30 seconds
    refetchInterval: (query) => computeConnectorDetailsRefetchInterval(query.state.data as any),
    refetchIntervalInBackground: false,
  });

  return {
    ...query,
    // Convenience accessors for the extended response fields
    connector: query.data?.connector,
    credentialFlags: query.data?.credentialFlags,
    connection: query.data?.connection,
    etag: query.data?.etag,
    oauthIdentity: query.data?.oauthIdentity ?? null,
  };
}

/**
 * Hook to update connector configuration
 */
export function useUpdateConnector() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ connectorType, name, config }: {
      connectorType: ConnectorType;
      name: string;
      config: Partial<ConnectorConfig>;
    }) => updateConnector(connectorType, name, config),
    onSuccess: (_data, variables) => {
      // Invalidate both the list and the individual connector query
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
      queryClient.invalidateQueries({ queryKey: ['connector', variables.connectorType, variables.name] });
    },
  });
}

/**
 * Hook to save the expected leaf client roster for a google_ads connector
 * (#870). The endpoint normalizes IDs to digit-only, dedupes, and sorts;
 * the response is the authoritative post-save roster.
 */
export function useUpdateExpectedLeafRoster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ connectorType, name, clientIds, severities, mutedClientIds }: {
      connectorType: string;
      name: string;
      clientIds: string[];
      severities?: Record<string, string>;
      mutedClientIds?: string[];
    }) => {
      const res = await apiClient.put(
        `${API_BASE}/${connectorType}/${encodeURIComponent(name)}/expected-leaf-roster`,
        {
          client_ids: clientIds,
          severities: severities ?? {},
          muted_client_ids: mutedClientIds ?? [],
        },
      );
      return res.data as {
        expected_leaf_client_ids: string[];
        leaf_client_severities: Record<string, string>;
        muted_leaf_client_ids: string[];
      };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['connector', variables.connectorType, variables.name] });
    },
  });
}

/**
 * Rename an existing connector via the config-id-addressed configurations
 * endpoint (the /{type}/{name} PATCH route can't rename — name is its key).
 */
async function renameConnector(configId: string, newName: string): Promise<{ id: string; name: string }> {
  const response = await apiClient.put(`${API_BASE}/configurations/${configId}`, { name: newName });
  return response.data;
}

/**
 * Hook to rename a connector. On success the caller navigates to the new URL
 * (the connector is addressed by name), so we only invalidate the list.
 */
export function useRenameConnector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, newName }: { configId: string; newName: string }) =>
      renameConnector(configId, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
  });
}

/**
 * Hook to update connector credentials with ETag-based optimistic concurrency control.
 *
 * This hook handles credential updates for credential-editing connectors (google_ads, s3_csv, vistar)
 * with proper 409 Conflict error handling for concurrent modifications.
 *
 * @param options - Optional configuration including onConflict callback
 * @returns React Query mutation object with mutate/mutateAsync functions
 *
 * @example
 * ```tsx
 * const { mutate, isPending, error } = useUpdateCredentials({
 *   onConflict: (conflictError) => {
 *     // Handle conflict - e.g., show refresh dialog or auto-refresh
 *     console.log('Current ETag:', conflictError.currentEtag);
 *   },
 * });
 *
 * mutate({
 *   connectorType: 's3_csv',
 *   name: 'my-connection',
 *   credentials: { bucket: 'new-bucket' },
 *   etag: currentEtag,
 * });
 * ```
 */
export function useUpdateCredentials(options?: UpdateCredentialsOptions) {
  const queryClient = useQueryClient();
  const { onConflict } = options || {};

  return useMutation({
    mutationFn: (params: UpdateCredentialsParams) => updateCredentials(params),
    onSuccess: (_data, variables) => {
      // Invalidate connector queries to refresh data with new ETag
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
      queryClient.invalidateQueries({ queryKey: ['connector', variables.connectorType, variables.name] });
    },
    onError: (error) => {
      // Call onConflict callback for 409 Conflict errors
      if (isConflictError(error) && onConflict) {
        onConflict(error);
      }
    },
  });
}

/**
 * Upload query file for a connector
 */
async function uploadQueryFile(
  connectorType: ConnectorType,
  name: string,
  yamlContent: string,
  validateOnly: boolean = false
): Promise<{
  uploaded: boolean;
  validation_result: {
    valid: boolean;
    errors: string[];
    warnings: string[];
    query_count: number;
  };
  uploaded_at?: string;
  message: string;
}> {
  const response = await apiClient.post(
    `${API_BASE}/${connectorType}/${encodeURIComponent(name)}/query-file`,
    { yaml_content: yamlContent, validate_only: validateOnly }
  );
  return response.data;
}

/**
 * Get query file for a connector
 */
async function getQueryFile(
  connectorType: ConnectorType,
  name: string
): Promise<{
  yaml_content: string;
  parsed_queries: any;
  uploaded_at?: string;
  validated_at?: string;
} | null> {
  try {
    const response = await apiClient.get(`${API_BASE}/${connectorType}/${encodeURIComponent(name)}/query-file`);
    return response.data;
  } catch (error: any) {
    // Return null if no query file exists (404)
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Delete query file for a connector
 */
async function deleteQueryFile(connectorType: ConnectorType, name: string): Promise<void> {
  await apiClient.delete(`${API_BASE}/${connectorType}/${encodeURIComponent(name)}/query-file`);
}

/**
 * Hook to upload query file
 */
export function useUploadQueryFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ connectorType, name, yamlContent, validateOnly }: {
      connectorType: ConnectorType;
      name: string;
      yamlContent: string;
      validateOnly?: boolean;
    }) => uploadQueryFile(connectorType, name, yamlContent, validateOnly),
    onSuccess: (_data, variables) => {
      // Invalidate connector queries to refresh query file data
      queryClient.invalidateQueries({ queryKey: ['connector', variables.connectorType, variables.name] });
      queryClient.invalidateQueries({ queryKey: ['queryFile', variables.connectorType, variables.name] });
    },
  });
}

/**
 * Hook to get query file
 */
export function useQueryFile(connectorType: string, name: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['queryFile', connectorType, name],
    queryFn: () => getQueryFile(connectorType as ConnectorType, name),
    enabled: enabled && !!connectorType && !!name,
    staleTime: 30000,
  });
}

/**
 * Delete a connector configuration by its ID.
 */
async function deleteConnector(configId: string): Promise<void> {
  await apiClient.delete(`${API_BASE}/configurations/${encodeURIComponent(configId)}`);
}

/**
 * Hook to delete a connector configuration.
 *
 * Calls DELETE /connectors/configurations/{config_id} and invalidates
 * the connector list cache on success so the UI refreshes.
 *
 * @example
 * ```tsx
 * const deleteConnectorMutation = useDeleteConnector();
 *
 * deleteConnectorMutation.mutate({ configId: connector.id });
 * ```
 */
export function useDeleteConnector() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ configId }: { configId: string }) => deleteConnector(configId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
  });
}

/**
 * Hook to delete query file
 */
export function useDeleteQueryFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ connectorType, name }: { connectorType: ConnectorType; name: string }) =>
      deleteQueryFile(connectorType, name),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['connector', variables.connectorType, variables.name] });
      queryClient.invalidateQueries({ queryKey: ['queryFile', variables.connectorType, variables.name] });
    },
  });
}

/**
 * Discovered Google Analytics property
 */
export interface DiscoveredProperty {
  property_id: string;
  display_name?: string;
  account_id?: string;
  account_display_name?: string;
  time_zone?: string;
}

/**
 * Response from Google Analytics property discovery
 */
export interface DiscoverPropertiesResponse {
  properties: DiscoveredProperty[];
  total: number;
}

/**
 * Credentials for Google Analytics property discovery
 */
export interface GoogleAnalyticsCredentials {
  client_id: string;
  client_secret: string;
  refresh_token: string;
}

/**
 * Discover Google Analytics properties using provided credentials
 */
async function discoverGoogleAnalyticsProperties(
  credentials: GoogleAnalyticsCredentials
): Promise<DiscoverPropertiesResponse> {
  const response = await apiClient.post(
    `${API_BASE}/google_analytics/discover-properties`,
    credentials
  );
  return response.data;
}

/**
 * Hook to discover Google Analytics properties
 */
export function useDiscoverGoogleAnalyticsProperties() {
  return useMutation({
    mutationFn: (credentials: GoogleAnalyticsCredentials) => discoverGoogleAnalyticsProperties(credentials),
  });
}

export interface PreviewMatchedSheet {
  id: string;
  name: string;
  parent_folder_name: string;
  modified_time?: string | null;
  owner_email?: string | null;
  /** #802: canonical Drive URL for linking the preview row to the file. */
  web_view_link?: string | null;
}

export interface PreviewSkippedSheet {
  name: string;
  parent_folder_name: string;
  reason: string;
}

export interface PreviewDriveSheetsResponse {
  matched: PreviewMatchedSheet[];
  matched_total: number;
  skipped_total: number;
  skipped_sample: PreviewSkippedSheet[];
  scanned: number;
  truncated: boolean;
  max_depth: number;
  /**
   * #1643: Drive children the walk could not attribute to a requested parent and
   * therefore dropped. Non-zero means `matched` may be INCOMPLETE. Optional so an
   * older API build that omits the field does not break the type.
   */
  unattributed_children?: number;
}

export interface PreviewDriveSheetsRequest {
  credentials?: Record<string, unknown>;
  settings: Record<string, unknown>;
  /** Editor mode (#501): resolve stored credentials for this saved connector. */
  connector_name?: string;
}

async function previewDriveSheets(
  req: PreviewDriveSheetsRequest
): Promise<PreviewDriveSheetsResponse> {
  const response = await apiClient.post(
    `${API_BASE}/google_sheets/preview-drive-sheets`,
    req
  );
  return response.data;
}

/** Hook to preview Google Sheets matched by Drive-discovery criteria. */
export function usePreviewDriveSheets() {
  return useMutation({
    mutationFn: (req: PreviewDriveSheetsRequest) => previewDriveSheets(req),
  });
}

/**
 * Fetch connector schedule configuration
 */
async function fetchConnectorSchedule(
  connectorType: ConnectorType,
  name: string
): Promise<ConnectorSchedule> {
  const response = await apiClient.get(`${API_BASE}/${connectorType}/${encodeURIComponent(name)}/schedule`);
  return response.data;
}

/**
 * Update connector schedule configuration
 */
async function updateConnectorSchedule(
  connectorType: ConnectorType,
  name: string,
  schedule: ScheduleUpdateRequest
): Promise<ConnectorSchedule> {
  const response = await apiClient.put(`${API_BASE}/${connectorType}/${encodeURIComponent(name)}/schedule`, schedule);
  return response.data;
}

/**
 * Preview upcoming schedule runs
 */
async function previewSchedule(
  connectorType: ConnectorType,
  name: string,
  cronExpression: string,
  timezone: string = DEFAULT_SCHEDULE_TIMEZONE,
  count: number = 5
): Promise<SchedulePreviewResponse> {
  const response = await apiClient.post(`${API_BASE}/${connectorType}/${encodeURIComponent(name)}/schedule/preview`, {
    cron_expression: cronExpression,
    timezone,
    count,
  });
  return response.data;
}

/**
 * Hook to fetch connector schedule
 */
export function useConnectorSchedule(connectorType: string, name: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['schedule', connectorType, name],
    queryFn: () => fetchConnectorSchedule(connectorType as ConnectorType, name),
    enabled: enabled && !!connectorType && !!name,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to update connector schedule
 */
export function useUpdateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ connectorType, name, schedule }: {
      connectorType: ConnectorType;
      name: string;
      schedule: ScheduleUpdateRequest;
    }) => updateConnectorSchedule(connectorType, name, schedule),
    onSuccess: (_data, variables) => {
      // Invalidate schedule query
      queryClient.invalidateQueries({ queryKey: ['schedule', variables.connectorType, variables.name] });
      // Invalidate connector details to update UI
      queryClient.invalidateQueries({ queryKey: ['connector', variables.connectorType, variables.name] });
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
  });
}

/**
 * Hook to preview schedule runs - uses debouncing for live preview as user types
 */
export function useSchedulePreview(
  connectorType: string,
  name: string,
  cronExpression: string,
  timezone: string = DEFAULT_SCHEDULE_TIMEZONE,
  count: number = 5,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['schedulePreview', connectorType, name, cronExpression, timezone, count],
    queryFn: () => previewSchedule(connectorType as ConnectorType, name, cronExpression, timezone, count),
    enabled: enabled && !!connectorType && !!name && !!cronExpression && cronExpression.trim().length > 0,
    staleTime: 300000, // 5 minutes - previews don't change often
    retry: false, // Don't retry on invalid cron expressions
  });
}

